import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ShieldCheck, ShieldAlert, ChevronRight, ChevronLeft, Plus, Pencil, Trash2, Check,
  Info, Calendar, Clock, Lock, CornerDownRight, Smartphone, HardHat, AlertTriangle,
} from 'lucide-react';
import { useSiteContext } from '../../SiteContext';
import { useSelectPayPeriod } from '../../hooks/useSelectPayPeriod';
import { PayPeriod } from '../../interfaces/PayPeriodInterface';
import { SegmentedControl } from '../../components/v2/adminManagement/AdminManagementKit';
import ConfirmModal from '../../components/ConfirmModal';
import { PageHead, Banner, EmptyState } from './workOrders/woShared';
import * as SafetyAPI from '../../api/safetyMessages';
import { can } from '../../util/adminPermissions';
import { SafetyMessage } from '../../api/safetyMessages';
import {
  ymd, todayYmd, weekRangeLabel, shortDayLabel, relativeWeekLabel,
  periodPosition, safetyStatus, STATUS_LABEL, PeriodPosition, SafetyStatus,
} from './safetyMessageHelpers';
import './SafetyMessagePage.css';

/**
 * Weekly Safety Message — Admin App configuration page (TAS-128).
 *
 * One message per pay period, authored here and delivered by the mobile app as a
 * tile on the Home screen for the whole period plus a popup on the employee's
 * first clock-in of that period.
 *
 * Built from docs/featureBriefs/design_handoff_safety_message_admin. Four things
 * in here are product decisions rather than implementation choices, and undoing
 * any of them changes what the feature promises:
 *
 *  1. NOTHING IS RECORDED SERVER-SIDE. There is no acknowledgement table, no
 *     roster, no read count, and no way to know who saw a message. The popup's
 *     "already seen this week" state lives in AsyncStorage ON THE DEVICE. The
 *     consequence is stated to the admin in the editor banner: editing a live
 *     week updates the tile, but can never re-fire the popup for anyone who
 *     already dismissed it. Do not add UI that implies otherwise.
 *  2. THERE IS NO DRAFT STATE. Saving publishes — the API writes Published = 1 —
 *     because the pay period IS the schedule. Status therefore describes time
 *     (Live / Ready / Sent), not workflow.
 *  3. THE GLOBAL WEEK PILL IS THE ONLY WEEK CONTROL. TopBarV2 renders it
 *     unconditionally and there is no opt-out, so a second page-level week picker
 *     would compete with it. Row clicks move the pill; the pill moves the page.
 *  4. SPANISH IS OPTIONAL AND FALLS BACK SILENTLY. A Spanish-speaking employee
 *     with no translation sees the English text, with no badge and no error. The
 *     ES preview shows exactly that, which is the most persuasive way to tell an
 *     admin the translation is missing.
 *
 * PAY PERIOD START DAY IS DATA, NOT MONDAY. 20/20 P&H runs Mon→Sun, NP Mechanical
 * Thu→Wed. Every label renders from StartDate/EndDate — see safetyMessageHelpers.
 */

type ViewMode = 'list' | 'editor';

interface Draft {
  title: string;
  title_es: string;
  body: string;
  body_es: string;
}

const EMPTY_DRAFT: Draft = { title: '', title_es: '', body: '', body_es: '' };

/**
 * How much of the calendar the list shows, either side of the current pay period.
 *
 * Ahead is the working range — writing next quarter's message early is a thing this
 * page is for. Back is only enough to check what recently went out; older weeks are
 * kept in the table and readable by the API, just not listed.
 */
const WEEKS_BACK = 5;
const WEEKS_AHEAD = 12;

function draftFrom(message: SafetyMessage | undefined): Draft {
  if (!message) return { ...EMPTY_DRAFT };
  return {
    title: message.title ?? '',
    title_es: message.title_es ?? '',
    body: message.body ?? '',
    body_es: message.body_es ?? '',
  };
}

/** Counter turns amber near the limit and red at it. Copied from MiscTaskDialogs.tsx:41. */
function countClass(n: number, max: number): string {
  if (n >= max) return 'cnt full';
  return n >= max * 0.9 ? 'cnt near' : 'cnt';
}

/** A message counts as translated only when BOTH Spanish fields carry text — matching
 *  the API's hasSpanish(). A half-translation would otherwise show a green ES chip
 *  down the list while employees still read English for one of the two fields. */
function hasSpanish(message: SafetyMessage | undefined): boolean {
  return !!message && !!message.title_es?.trim() && !!message.body_es?.trim();
}

/* ====================================================================== *
 * Form row — copied, not imported
 *
 * The original (TimeOffConfigurationPage.tsx:360-377) is a module-private const
 * with .toc-scoped CSS, so there is nothing to import.
 * ====================================================================== */

const FRow: React.FC<{
  label: string;
  required?: boolean;
  optional?: boolean;
  counter?: React.ReactNode;
  hint?: React.ReactNode;
  children: React.ReactNode;
}> = ({ label, required, optional, counter, hint, children }) => (
  <div className="sfm-frow">
    <label>
      <span>
        {label}
        {required && <span className="req">*</span>}
        {optional && <span className="opt">optional</span>}
      </span>
      {counter}
    </label>
    {children}
    {hint && <div className="hint">{hint}</div>}
  </div>
);

/* ====================================================================== *
 * Phone preview
 *
 * A React re-implementation of design/sfm_hifi/sfm-phone.js + sfm-phone.css,
 * whose values are themselves taken from the MobileApp's CustomContainer.tsx
 * (90% width / padding 20 / border 2 / radius 15) and Popup.tsx (width 400 /
 * maxHeight 85% / body maxHeight 350).
 *
 * IN THE PROTOTYPE THIS FILE WAS SHARED with the mobile hi-fi, so the two could
 * not drift. IN PRODUCTION THAT GUARANTEE DOES NOT EXIST — this is a web preview
 * of React Native components that do not exist yet. When the MobileApp side is
 * built, the two must be kept in step by hand.
 *
 * Classes are prefixed .sfm-ph, NOT .ph — woShared's PageHead already owns .ph-ic.
 * ====================================================================== */

/** Rough proxy for "does numberOfLines={3} clamp this" at mobile width. Only decides
 *  whether the preview shows the Read more affordance. */
function willClamp(text: string): boolean {
  return text.length > 150;
}

function resolveForLang(draft: Draft, lang: 'en' | 'es'): { title: string; body: string } {
  const es = lang === 'es';
  return {
    title: (es && draft.title_es.trim()) ? draft.title_es : draft.title,
    body: (es && draft.body_es.trim()) ? draft.body_es : draft.body,
  };
}

const PhonePreview: React.FC<{
  draft: Draft;
  surface: 'popup' | 'tile';
  lang: 'en' | 'es';
  onReadMore: () => void;
}> = ({ draft, surface, lang, onReadMore }) => {
  const { title, body } = resolveForLang(draft, lang);
  const clamp = willClamp(body);

  const eyebrow = lang === 'es' ? 'Mensaje de seguridad' : 'Safety message';
  const more = lang === 'es' ? 'Leer más' : 'Read more';
  const ok = lang === 'es' ? 'Entendido' : 'OK, got it';
  const punch = lang === 'es' ? 'Entrada registrada a las' : 'Clocked in at';

  if (!title.trim() && !body.trim()) {
    return (
      <div className="sfm-ph sfm-ph-frame empty-preview">
        <div className="sfm-ph-nothing">Nothing to preview yet. Write a title and body.</div>
      </div>
    );
  }

  const card = (
    <div className="sfm-ph-card sfm-ph-safety">
      <div className="st-head">
        <div className="st-ic"><HardHat size={19} /></div>
        <div className="st-eyebrow">{eyebrow}</div>
      </div>
      <div className="st-title">{title}</div>
      <div className={`st-body${clamp ? ' clamp' : ''}`}>{body}</div>
      {clamp && (
        <button type="button" className="st-more" onClick={onReadMore}>
          {more}<ChevronRight size={16} />
        </button>
      )}
    </div>
  );

  if (surface === 'tile') {
    return (
      <div className="sfm-ph sfm-ph-frame">
        {/* Dimmed stand-ins so the tile is seen in its real context: the clock card
            sits above it and the Timesheet Overview tile below. */}
        <div className="sfm-ph-ghost">Clock</div>
        {card}
        <div className="sfm-ph-ghost">Timesheet Overview</div>
      </div>
    );
  }

  return (
    <div className="sfm-ph sfm-ph-frame scrim">
      <div className="sfm-ph-pop">
        <div className="pp-punch">
          <div className="pp-check"><Check size={26} /></div>
          <div>
            <div className="pl">{punch}</div>
            <div className="pv">6:58 AM</div>
          </div>
        </div>
        <div className="pp-eyebrow"><HardHat size={16} />{eyebrow}</div>
        <div className="pp-title">{title}</div>
        <div className="pp-scroll"><div className="pp-body">{body}</div></div>
        <button type="button" className="pp-ok">{ok}</button>
      </div>
    </div>
  );
};

/* ====================================================================== *
 * The page
 * ====================================================================== */

const SafetyMessagePage: React.FC = () => {
  const { payPeriods, payPeriodID, userRole } = useSiteContext();
  const selectPayPeriod = useSelectPayPeriod();

  const [messages, setMessages] = useState<SafetyMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [view, setView] = useState<ViewMode>('list');
  const [editingPeriodID, setEditingPeriodID] = useState<number | null>(null);
  const [draft, setDraft] = useState<Draft>({ ...EMPTY_DRAFT });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [modal, setModal] = useState<null | 'delete' | 'discard'>(null);
  /**
   * Where the admin was heading when the dirty guard stopped them — either back to the
   * list, or to the week the global pill just moved to. Held so "Discard" can complete
   * the interrupted move and "Cancel" can undo it.
   */
  const [discardTo, setDiscardTo] = useState<{ kind: 'list' } | { kind: 'week'; payPeriodID: number } | null>(null);

  const [previewSurface, setPreviewSurface] = useState<'popup' | 'tile'>('popup');
  const [previewLang, setPreviewLang] = useState<'en' | 'es'>('en');

  const [toast, setToast] = useState<{ msg: string; err: boolean } | null>(null);
  const toastTimer = useRef<number | undefined>(undefined);

  const showToast = useCallback((msg: string, err = false) => {
    setToast({ msg, err });
    window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 3200);
  }, []);

  useEffect(() => () => window.clearTimeout(toastTimer.current), []);

  const today = todayYmd();

  /* ---------------- the visible window ---------------- */

  /**
   * Current week first, then upcoming ascending, then past most-recent-first.
   *
   * Bounded to WEEKS_BACK/WEEKS_AHEAD around the current period. 20/20 P&H has pay
   * periods generated years out, and an unbounded list buries the handful of weeks
   * anyone actually authors under hundreds of empty rows.
   */
  const orderedRows = useMemo(() => {
    const sorted = [...payPeriods].sort((a, b) => (ymd(a.StartDate) < ymd(b.StartDate) ? -1 : 1));
    return {
      current: sorted.filter((pp) => periodPosition(pp, today) === 'current'),
      upcoming: sorted.filter((pp) => periodPosition(pp, today) === 'future').slice(0, WEEKS_AHEAD),
      past: sorted.filter((pp) => periodPosition(pp, today) === 'past').reverse().slice(0, WEEKS_BACK),
    };
  }, [payPeriods, today]);

  /* ---------------- data ---------------- */

  /**
   * The date window to fetch messages for.
   *
   * Derived from the rows actually on screen, so the fetch and the list can never
   * disagree — no week renders without its message, and nothing is fetched that has
   * nowhere to show. Passed explicitly on every read because GET /admin/safetyMessages
   * defaults an omitted window to eight weeks either side of today, which is narrower
   * than WEEKS_AHEAD and would silently blank out the furthest upcoming rows.
   */
  const span = useMemo(() => {
    const visible = [...orderedRows.past, ...orderedRows.current, ...orderedRows.upcoming];
    // The global week pill can reach a period outside the listed window, and the editor
    // follows it there. Widen the fetch to cover it, or that week would look unwritten
    // and the save would come back 409 against a message the admin was never shown.
    const editing = payPeriods.find((pp) => pp.PayPeriodID === editingPeriodID);
    if (editing && !visible.some((pp) => pp.PayPeriodID === editing.PayPeriodID)) {
      visible.push(editing);
    }
    if (visible.length === 0) return null;
    let from = ymd(visible[0].StartDate);
    let to = ymd(visible[0].EndDate);
    for (const pp of visible) {
      const s = ymd(pp.StartDate);
      const e = ymd(pp.EndDate);
      if (s < from) from = s;
      if (e > to) to = e;
    }
    return { from, to };
  }, [orderedRows, payPeriods, editingPeriodID]);

  /** Bumped on every fetch so a slow earlier response cannot overwrite a newer one. */
  const loadSeq = useRef(0);

  const loadMessages = useCallback(async () => {
    if (!span) return;
    const seq = ++loadSeq.current;
    setLoading(true);
    setLoadError(null);
    try {
      const rows = await SafetyAPI.getSafetyMessages(span.from, span.to);
      if (seq === loadSeq.current) setMessages(rows);
    } catch (err) {
      if (seq === loadSeq.current) {
        setLoadError(SafetyAPI.getSafetyMessageErrorMessage(err, 'Failed to load safety messages.'));
      }
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [span]);

  useEffect(() => { loadMessages(); }, [loadMessages]);

  const messageByPeriod = useMemo(() => {
    const map = new Map<number, SafetyMessage>();
    for (const m of messages) map.set(m.payPeriodID, m);
    return map;
  }, [messages]);

  /* ---------------- period bookkeeping ---------------- */

  const currentPeriod = useMemo(
    () => payPeriods.find((pp) => periodPosition(pp, today) === 'current') ?? null,
    [payPeriods, today]
  );

  const periodByID = useMemo(() => {
    const map = new Map<number, PayPeriod>();
    for (const pp of payPeriods) map.set(pp.PayPeriodID, pp);
    return map;
  }, [payPeriods]);

  const openEditorFor = useCallback((pp: PayPeriod) => {
    setEditingPeriodID(pp.PayPeriodID);
    setDraft(draftFrom(messageByPeriod.get(pp.PayPeriodID)));
    setDirty(false);
    setSaveError(null);
    setView('editor');
    window.scrollTo(0, 0);
  }, [messageByPeriod]);

  const openRow = useCallback((pp: PayPeriod) => {
    // Row click also moves the global pill, so the shell and the page agree on the week.
    selectPayPeriod(pp);
    openEditorFor(pp);
  }, [selectPayPeriod, openEditorFor]);

  /** Leaves the editor unconditionally. Callers that can be interrupted use goList(). */
  const forceGoList = useCallback(() => {
    setView('list');
    setDirty(false);
    setSaveError(null);
  }, []);

  const goList = useCallback(() => {
    if (dirty) {
      setDiscardTo({ kind: 'list' });
      setModal('discard');
      return;
    }
    forceGoList();
  }, [dirty, forceGoList]);

  /**
   * The global pill moved while the editor was open.
   *
   * Clean editor: follow it. Dirty editor: ask, and if the admin cancels, put the
   * pill back where it was — the alternative is a pill and an editor pointing at
   * different weeks, which is worse than either outcome.
   */
  useEffect(() => {
    if (view !== 'editor' || editingPeriodID == null) return;
    if (payPeriodID === editingPeriodID) return;
    const next = periodByID.get(payPeriodID);
    if (!next) return;
    if (!dirty) {
      openEditorFor(next);
      return;
    }
    setDiscardTo({ kind: 'week', payPeriodID });
    setModal('discard');
  }, [payPeriodID, view, editingPeriodID, dirty, periodByID, openEditorFor]);

  /** Re-sync the draft after a save/delete refetch, but never over live typing. */
  useEffect(() => {
    if (view !== 'editor' || editingPeriodID == null || dirty) return;
    setDraft(draftFrom(messageByPeriod.get(editingPeriodID)));
  }, [messageByPeriod, view, editingPeriodID, dirty]);

  const editingPeriod = editingPeriodID != null ? periodByID.get(editingPeriodID) ?? null : null;
  const editingMessage = editingPeriodID != null ? messageByPeriod.get(editingPeriodID) : undefined;
  const editingPosition: PeriodPosition | null = editingPeriod ? periodPosition(editingPeriod, today) : null;
  const readOnly = editingPosition === 'past';

  const setField = useCallback((field: keyof Draft, value: string) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
    setDirty(true);
    setSaveError(null);
  }, []);

  /* ---------------- mutations ---------------- */

  const handleSave = useCallback(async () => {
    if (!editingPeriod || readOnly) return;
    const title = draft.title.trim();
    const body = draft.body.trim();
    if (!title || !body) {
      setSaveError('An English title and body are required.');
      return;
    }

    setSaving(true);
    setSaveError(null);
    const payload = {
      title,
      body,
      title_es: draft.title_es.trim() || null,
      body_es: draft.body_es.trim() || null,
    };

    try {
      if (editingMessage) {
        await SafetyAPI.updateSafetyMessage(editingMessage.safetyMessageID, payload);
      } else {
        await SafetyAPI.createSafetyMessage({ payPeriodID: editingPeriod.PayPeriodID, ...payload });
      }
      // Refetch BEFORE clearing dirty. The re-sync effect below only runs on a clean
      // editor, so clearing first would briefly repopulate the fields from the stale
      // pre-save message and flash the old text back at the admin.
      await loadMessages();
      setDirty(false);
      showToast(
        editingPosition === 'current'
          ? 'Saved — live on phones now'
          : `Saved — goes out ${shortDayLabel(editingPeriod.StartDate)}`
      );
    } catch (err) {
      const message = SafetyAPI.getSafetyMessageErrorMessage(err);
      if (SafetyAPI.getSafetyMessageErrorStatus(err) === 409) {
        // Someone else authored this week while this editor was open. Inline, because
        // the fix is to reload and look at what they wrote — not to retry this save.
        setSaveError(
          `A safety message already exists for ${weekRangeLabel(editingPeriod.StartDate, editingPeriod.EndDate)}. ` +
          'Someone else created it while this was open — reload to see it.'
        );
      }
      showToast(message, true);
    } finally {
      setSaving(false);
    }
  }, [editingPeriod, editingMessage, editingPosition, draft, readOnly, loadMessages, showToast]);

  const handleDelete = useCallback(async () => {
    if (!editingMessage) return;
    try {
      await SafetyAPI.deleteSafetyMessage(editingMessage.safetyMessageID);
      setModal(null);
      setDirty(false);
      await loadMessages();
      // forceGoList, not goList: the message is gone, so there is nothing left to discard
      // and a confirm here would be asking about edits to a row that no longer exists.
      forceGoList();
      showToast('Message deleted');
    } catch (err) {
      setModal(null);
      showToast(SafetyAPI.getSafetyMessageErrorMessage(err, "Couldn't delete the message. Try again."), true);
    }
  }, [editingMessage, loadMessages, forceGoList, showToast]);

  const copyEnglishAcross = useCallback(() => {
    setDraft((prev) => ({ ...prev, title_es: prev.title, body_es: prev.body }));
    setDirty(true);
    showToast('English copied into the Spanish fields — translate before saving');
  }, [showToast]);

  /* ---------------- gates ---------------- */

  // Defensive: App.tsx already redirects roles without ManageSafetyMessages to Home.
  if (!can(userRole, 'manageSafetyMessages')) {
    return (
      <div className="sfm">
        <EmptyState Icon={Lock} title="Not authorized" style={{ marginTop: 40 }}>
          The Safety Message page requires the Manage safety messages permission. Ask an
          administrator if you need access.
        </EmptyState>
      </div>
    );
  }

  /* ---------------- list ---------------- */

  const currentMessage = currentPeriod ? messageByPeriod.get(currentPeriod.PayPeriodID) : undefined;

  const statusPill = (status: SafetyStatus) => (
    <span className={`sfm-pill ${status}`}><span className="dot" />{STATUS_LABEL[status]}</span>
  );

  const renderRow = (pp: PayPeriod, emphasis: boolean) => {
    const message = messageByPeriod.get(pp.PayPeriodID);
    const position = periodPosition(pp, today);
    const status = safetyStatus(position, !!message);
    const lastEdited = message?.modifiedDate ?? message?.createdDate ?? null;
    const lastBy = message?.modifiedBy ?? message?.createdBy ?? null;

    return (
      <tr
        key={pp.PayPeriodID}
        className={emphasis ? `sfm-row-current${message ? '' : ' none'}` : undefined}
        onClick={() => openRow(pp)}
      >
        <td className="wk">
          <div className="dates">{weekRangeLabel(pp.StartDate, pp.EndDate)}</div>
          {emphasis
            ? <span className="thisweek">This week</span>
            : <div className="rel">{currentPeriod ? relativeWeekLabel(pp.StartDate, currentPeriod.StartDate) : ''}</div>}
        </td>
        <td className="msg">
          {message ? (
            <>
              <div className="t">{message.title}</div>
              <div className="snip">{message.body}</div>
            </>
          ) : <span className="dash">—</span>}
        </td>
        <td>{statusPill(status)}</td>
        <td className="es">
          {message
            ? (hasSpanish(message) ? <span className="es-chip">ES</span> : <span className="es-chip absent">—</span>)
            : null}
        </td>
        <td className="edited">
          {lastEdited ? (
            <>
              <div>{shortDayLabel(lastEdited)}</div>
              {lastBy && <div className="by">{lastBy}</div>}
            </>
          ) : <span className="dash">—</span>}
        </td>
        <td className="chev"><ChevronRight size={18} /></td>
      </tr>
    );
  };

  const renderList = () => {
    if (loading) {
      return <div className="sfm-loading">Loading safety messages…</div>;
    }
    if (loadError) {
      return <Banner variant="danger" Icon={AlertTriangle}>{loadError}</Banner>;
    }
    if (payPeriods.length === 0) {
      return (
        <EmptyState Icon={Calendar} title="No pay periods">
          This tenant has no pay periods yet, so there is nothing to write a message against.
        </EmptyState>
      );
    }
    return (
      <>
        {/* First run. The handoff (§5, screenshot 08) makes this a full-page empty state
            that REPLACES the table — but the table is the only way to reach a week other
            than the current one, so on a fresh tenant that leaves every future week
            unauthorable and the page looking broken. The guidance is worth keeping, so it
            becomes a strip above the list rather than a substitute for it. */}
        {messages.length === 0 && (
          <div className="sfm-firstrun">
            <div className="eic"><ShieldCheck /></div>
            <div>
              <div className="et">No safety messages yet</div>
              <p>
                Write one for this week and it goes out to every employee's phone at the start of
                the pay period. One message per week — past weeks stay here as a record.
              </p>
            </div>
          </div>
        )}
        <div className="sfm-listcard">
          <div className="sfm-tbl-wrap">
            <table className="sfm-dt">
              <thead>
                <tr>
                  <th>Pay period</th>
                  <th>Message</th>
                  <th>Status</th>
                  <th className="cen">Spanish</th>
                  <th>Last edited</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {orderedRows.current.map((pp) => renderRow(pp, true))}
                {orderedRows.upcoming.length > 0 && (
                  <tr className="sfm-grouprow"><td colSpan={6}>Upcoming</td></tr>
                )}
                {orderedRows.upcoming.map((pp) => renderRow(pp, false))}
                {orderedRows.past.length > 0 && (
                  <tr className="sfm-grouprow"><td colSpan={6}>Past</td></tr>
                )}
                {orderedRows.past.map((pp) => renderRow(pp, false))}
              </tbody>
            </table>
          </div>
        </div>
      </>
    );
  };

  /* ---------------- editor ---------------- */

  const renderEditor = () => {
    if (!editingPeriod) return null;
    const range = weekRangeLabel(editingPeriod.StartDate, editingPeriod.EndDate);
    const status = safetyStatus(editingPosition!, !!editingMessage);
    const spanishBlank = !draft.title_es.trim() || !draft.body_es.trim();

    return (
      <>
        <div className="sfm-crumb">
          <button type="button" className="backlink" onClick={goList}>
            <ChevronLeft size={15} />Safety Message
          </button>
          <ChevronRight size={15} className="cx" />
          <span className="here">{range}</span>
        </div>

        {editingPosition === 'current' && editingMessage && (
          <Banner variant="warn" Icon={AlertTriangle}>
            <b>This message is live and the week is in progress.</b> The tile will update on every
            phone, but anyone who already dismissed the popup will not see it again — that
            dismissal is stored on their device and nothing here can undo it. For a correction
            that has to be seen, tell a supervisor as well.
          </Banner>
        )}
        {editingPosition === 'past' && (
          <Banner variant="neutral" Icon={Clock}>
            <b>This pay period has ended.</b> Editing it changes nothing anyone will see — the
            tile is gone from every phone and the popup already fired.
          </Banner>
        )}

        <div className="sfm-edgrid">
          <div className="sfm-edcard">
            <div className="sfm-edhead">
              <div className="grow">
                <div className="dates">{range}</div>
                <div className={`rel${editingPosition === 'current' ? ' now' : ''}`}>
                  {currentPeriod ? relativeWeekLabel(editingPeriod.StartDate, currentPeriod.StartDate) : ''}
                </div>
              </div>
              {statusPill(status)}
              <div className="edited">
                {editingMessage ? (
                  <>
                    Last edited {shortDayLabel(editingMessage.modifiedDate ?? editingMessage.createdDate)}
                    {(editingMessage.modifiedBy ?? editingMessage.createdBy) && (
                      <div className="by">{editingMessage.modifiedBy ?? editingMessage.createdBy}</div>
                    )}
                  </>
                ) : 'Never edited'}
              </div>
            </div>

            <div className="sfm-bicols">
              <div className="sfm-langcol">
                <div className="sfm-lang">
                  <span className="code en">EN</span>
                  <span className="name">English</span>
                  <span className="flag req">Required</span>
                </div>
                <FRow
                  label="Title"
                  required
                  counter={<span className={countClass(draft.title.length, SafetyAPI.SAFETY_TITLE_MAX)}>
                    {draft.title.length} / {SafetyAPI.SAFETY_TITLE_MAX}
                  </span>}
                >
                  <input
                    className="sfm-input"
                    value={draft.title}
                    readOnly={readOnly}
                    maxLength={SafetyAPI.SAFETY_TITLE_MAX}
                    onChange={(e) => setField('title', e.target.value)}
                  />
                </FRow>
                <FRow
                  label="Body"
                  required
                  counter={<span className={countClass(draft.body.length, SafetyAPI.SAFETY_BODY_MAX)}>
                    {draft.body.length.toLocaleString()} / {SafetyAPI.SAFETY_BODY_MAX.toLocaleString()}
                  </span>}
                  hint="Shown in full in the popup and clamped to three lines on the tile."
                >
                  <textarea
                    className="sfm-input"
                    value={draft.body}
                    readOnly={readOnly}
                    maxLength={SafetyAPI.SAFETY_BODY_MAX}
                    placeholder="Plain text. No formatting, links, or images — it is read on a phone in bright sun."
                    onChange={(e) => setField('body', e.target.value)}
                  />
                </FRow>
                {saveError && <div className="sfm-inline-error">{saveError}</div>}
              </div>

              <div className="sfm-langcol es">
                <div className="sfm-lang">
                  <span className="code es">ES</span>
                  <span className="name">Spanish</span>
                  <span className="flag opt">Optional</span>
                  {!readOnly && (
                    <button type="button" className="sfm-copyacross" onClick={copyEnglishAcross}>
                      <CornerDownRight size={14} />Copy English across
                    </button>
                  )}
                </div>
                {spanishBlank && (
                  <div className="sfm-esnotice">
                    <Info size={16} />
                    <div>
                      <b>Spanish is blank.</b> Spanish-speaking employees will see the English text
                      — no error, no note on their phone. Check the ES preview to see exactly what
                      they get.
                    </div>
                  </div>
                )}
                <FRow
                  label="Title"
                  optional
                  counter={<span className={countClass(draft.title_es.length, SafetyAPI.SAFETY_TITLE_MAX)}>
                    {draft.title_es.length} / {SafetyAPI.SAFETY_TITLE_MAX}
                  </span>}
                >
                  <input
                    className="sfm-input"
                    value={draft.title_es}
                    readOnly={readOnly}
                    maxLength={SafetyAPI.SAFETY_TITLE_MAX}
                    onChange={(e) => setField('title_es', e.target.value)}
                  />
                </FRow>
                <FRow
                  label="Body"
                  optional
                  counter={<span className={countClass(draft.body_es.length, SafetyAPI.SAFETY_BODY_MAX)}>
                    {draft.body_es.length.toLocaleString()} / {SafetyAPI.SAFETY_BODY_MAX.toLocaleString()}
                  </span>}
                >
                  <textarea
                    className="sfm-input"
                    value={draft.body_es}
                    readOnly={readOnly}
                    maxLength={SafetyAPI.SAFETY_BODY_MAX}
                    onChange={(e) => setField('body_es', e.target.value)}
                  />
                </FRow>
              </div>
            </div>

            <div className="sfm-edbar">
              {readOnly ? (
                <>
                  <div className="sfm-readonly"><b>Read-only.</b> Past periods are kept as a record.</div>
                  <button type="button" className="sfm-btn" onClick={goList}>Back to all weeks</button>
                </>
              ) : (
                <>
                  <div className="sfm-edstatus">
                    {editingMessage && editingPosition === 'current' && (<><Check size={16} />Live on phones now</>)}
                    {editingPosition === 'future' && (<><Calendar size={16} />Goes out {shortDayLabel(editingPeriod.StartDate)}</>)}
                    {!editingMessage && editingPosition === 'current' && (<><ShieldAlert size={16} />Not written yet</>)}
                  </div>
                  <div className="sfm-edactions">
                    {editingMessage && (
                      <button type="button" className="sfm-btn ghost" onClick={() => setModal('delete')}>
                        <Trash2 size={16} />Delete
                      </button>
                    )}
                    <button type="button" className="sfm-btn primary" onClick={handleSave} disabled={saving}>
                      <Check size={16} />{saving ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="sfm-preview">
            <div className="sfm-preview-head">
              <Smartphone size={16} />
              <SegmentedControl
                options={[{ value: 'popup', label: 'Popup' }, { value: 'tile', label: 'Tile' }]}
                value={previewSurface}
                onChange={setPreviewSurface}
              />
              <div className="grow" />
              <SegmentedControl
                options={[{ value: 'en', label: 'EN' }, { value: 'es', label: 'ES' }]}
                value={previewLang}
                onChange={setPreviewLang}
              />
            </div>
            <div className="sfm-preview-body">
              <PhonePreview
                draft={draft}
                surface={previewSurface}
                lang={previewLang}
                onReadMore={() => setPreviewSurface('popup')}
              />
            </div>
            <div className="sfm-preview-foot">
              {previewLang === 'es' && spanishBlank ? (
                <><b>Spanish is blank</b>, so this is exactly what a Spanish-language employee sees:
                  the English text, with no note and no error.</>
              ) : previewSurface === 'popup' ? (
                <>Fires once, on the first clock-in this pay period. The punch confirmation is
                  folded into the same popup.</>
              ) : (
                <>On the Home screen all week, below the clock. Long bodies clamp to three lines
                  with <b>Read more</b>.</>
              )}
            </div>
          </div>
        </div>
      </>
    );
  };

  /* ---------------- shell ---------------- */

  const headAction = currentPeriod ? (
    <div className="sfm-headaction">
      <button type="button" className="sfm-btn primary" onClick={() => openRow(currentPeriod)}>
        {currentMessage ? <Pencil size={16} /> : <Plus size={16} />}
        {currentMessage ? "Edit this week's message" : "Write this week's message"}
      </button>
      <div className="hint">{weekRangeLabel(currentPeriod.StartDate, currentPeriod.EndDate)}</div>
    </div>
  ) : undefined;

  return (
    <div className="sfm">
      <div className="sfm-crumbs">Setup<ChevronRight size={14} /><span className="here">Safety Message</span></div>

      {view === 'list' ? (
        <>
          <PageHead
            Icon={ShieldCheck}
            title="Safety Message"
            sub="One message per pay period, published to every employee. It appears as a tile on the Home screen all week and as a popup on their first clock-in of the week."
            actions={headAction}
          />
          {!loading && !loadError && currentPeriod && !currentMessage && (
            <Banner
              variant="warn"
              Icon={ShieldAlert}
              action={
                <button type="button" className="sfm-bannerlink" onClick={() => openRow(currentPeriod)}>
                  Write it <ChevronRight size={14} />
                </button>
              }
            >
              <b>No safety message is published for this week.</b> Employees will not see one — no
              tile, no popup.
            </Banner>
          )}
          {renderList()}
        </>
      ) : renderEditor()}

      {modal === 'delete' && editingPeriod && (
        <ConfirmModal
          variant="error"
          icon={<Trash2 size={20} />}
          title="Delete this message?"
          subtitle="Frees the week for a new one"
          confirmLabel="Delete"
          detail={
            <>
              <div className="sfm-weekline">
                <Calendar size={15} />
                {weekRangeLabel(editingPeriod.StartDate, editingPeriod.EndDate)}
              </div>
              <p>
                The row is soft-deleted and the week goes back to <b>None</b>. If the period is
                under way, the tile comes off phones on the next refresh.
              </p>
            </>
          }
          onConfirm={handleDelete}
          onClose={() => setModal(null)}
        />
      )}

      {modal === 'discard' && (
        <ConfirmModal
          variant="warning"
          icon={<AlertTriangle size={20} />}
          title="Discard unsaved changes?"
          subtitle={discardTo?.kind === 'list' ? 'You are leaving the editor' : 'You moved to a different week'}
          confirmLabel="Discard"
          detail={<p>This week's edits have not been saved. Discarding leaves the message as it was.</p>}
          onConfirm={() => {
            const target = discardTo;
            setModal(null);
            setDiscardTo(null);
            if (target?.kind === 'week') {
              const next = periodByID.get(target.payPeriodID);
              if (next) openEditorFor(next);
            } else {
              forceGoList();
            }
          }}
          onClose={() => {
            const target = discardTo;
            setModal(null);
            setDiscardTo(null);
            // A cancelled PILL move has to put the pill back, or it and the editor would
            // point at different weeks. A cancelled "back to list" just stays put.
            if (target?.kind === 'week') {
              const back = editingPeriodID != null ? periodByID.get(editingPeriodID) : null;
              if (back) selectPayPeriod(back);
            }
          }}
        />
      )}

      {toast && <div className={`sfm-toast${toast.err ? ' err' : ''}`}>{toast.msg}</div>}
    </div>
  );
};

export default SafetyMessagePage;
